import { type Appearance } from '@stripe/stripe-js';
import { type ColorTokenKey, useThemeColors } from 'smoothie';

import { useActiveTheme } from '../../common/providers/DesignSystemProvider';

/**
 * Builds Stripe Elements' `appearance` config from this app's own design
 * tokens, resolved for the currently active theme — mirrors
 * usePaymentSheetAppearance() (native). Stripe Elements render in a
 * cross-origin iframe, which can't resolve our app's CSS custom
 * properties — useThemeColors()'s getColor() returns `var(--token)` on web,
 * which Stripe silently rejects — so this reads literal hex/rgba values via
 * getPrimitiveColor() instead, and will pick up dark mode automatically once
 * this app supports it (see useActiveTheme).
 */
export const useStripeElementsAppearance = ({
    isDisabled = false,
}: { isDisabled?: boolean } = {}): Appearance => {
    const [activeTheme] = useActiveTheme();
    const { getPrimitiveColor } = useThemeColors();
    const color = (token: ColorTokenKey) =>
        getPrimitiveColor(token, activeTheme);

    return {
        theme: 'flat',
        variables: {
            fontFamily: 'Figtree, sans-serif',
            colorPrimary: color('$gray1200'),
            colorText: color('$gray1200'),
            colorTextPlaceholder: color('$gray600'),
            colorBackground: 'transparent',
            colorDanger: color('$red1100'),
            borderRadius: '12px',
            spacingUnit: '4px',
        },
        rules: {
            '.Block': {
                backgroundColor: 'transparent',
                border: 'none',
                boxShadow: 'none',
            },
            '.AccordionItem': {
                backgroundColor: color('$gray100'),
                border: 'none',
                boxShadow: 'none',
                padding: '0',
            },
            '.Input': {
                backgroundColor: color(isDisabled ? '$gray400' : '$white'),
                border: `1px solid ${color(isDisabled ? '$gray600' : '$gray300')}`,
                boxShadow: 'none',
                padding: '14px 12px',
                ...(isDisabled ? { color: color('$gray1000') } : {}),
            },
            '.Input:focus': {
                border: `1px solid ${color(isDisabled ? '$gray600' : '$gray1200')}`,
                boxShadow: 'none',
            },
            '.Label': {
                color: color('$gray1000'),
                fontSize: '14px',
            },
            // Wallet tabs (e.g. Google Pay in Chrome) appear next to Card when the
            // browser supports them. The flat theme fills the selected tab with
            // colorPrimary (near-black), which hid the Card label — so style the
            // tabs explicitly: light surface, dark outline when selected.
            '.Tab': {
                backgroundColor: color('$gray200'),
                border: `1px solid ${color('$gray200')}`,
                boxShadow: 'none',
            },
            '.Tab:hover': {
                backgroundColor: color('$gray300'),
            },
            '.Tab--selected, .Tab--selected:hover, .Tab--selected:focus': {
                backgroundColor: color('$white'),
                border: `1px solid ${color('$gray1200')}`,
                boxShadow: 'none',
            },
            '.TabIcon': { fill: color('$gray1200') },
            '.TabIcon--selected': { fill: color('$gray1200') },
            '.TabLabel': { color: color('$gray1200') },
            '.TabLabel--selected': { color: color('$gray1200') },
        },
    };
};

export const STRIPE_BILLING_COUNTRY = 'US';

export const STRIPE_PAYMENT_ELEMENT_FIELDS = {
    billingDetails: { address: { country: 'never' } },
} as const;

export const STRIPE_CONFIRM_PARAMS = {
    payment_method_data: {
        billing_details: { address: { country: STRIPE_BILLING_COUNTRY } },
    },
};

export const STRIPE_ELEMENTS_FONTS = [
    {
        cssSrc: 'https://fonts.googleapis.com/css2?family=Figtree:wght@400;500;600&display=swap',
    },
];
